// @flow

import * as React from 'react';
import styled from '@emotion/styled';
import numeral from 'numeral';
import moment from 'moment';

import Modal from 'app/components/commons/Modal';
import ChallengeInfo from 'app/components/challenge/ChallengeInfo';
import Countdown from 'app/components/commons/Countdown';
import TicketIndicator from 'app/components/commons/TicketIndicator';
import TimerProvider from 'app/components/commons/TimerProvider';
import Tooltip from 'app/components/commons/Tooltip';
import { SCHEDULED, OPEN } from 'app/constants/challengeStatusTypes';
import Button from 'app/components/commons/Button';
import t from 'app/utils/translate';
import Responsive from 'app/components/responsive/Responsive';
import type { TabItem } from 'app/types';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';

type Props = {
  bonuses: Array<*>,
  cost: number,
  countries: Array<Object>,
  entryCap: number,
  entryCount: number,
  fetchGames: (id: string) => void,
  games?: Array<Object>,
  id: string,
  isTicketChallenge?: boolean,
  isRacing: boolean,
  isUnlimited: boolean,
  labels?: Array<Object>,
  lang: string,
  link: string,
  maxEntriesPerPerson: number,
  name: string,
  onClose: () => void,
  payout: string,
  payoutIcon?: string,
  payoutMessage: string,
  prizePool: string,
  scoring?: Array<Object>,
  startTime: number,
  statusId: number
};

type State = {
  activeTabItem: ?TabItem
};

const Head = styled.div(
  {
    display: 'flex',
    textTransform: 'uppercase',
    flexDirection: 'column'
  },
  ({ theme }) => ({
    backgroundColor: theme.color.basis2,
    color: theme.color.white,
    padding: theme.spacing.medium,
    [theme.mediaQuery.desktopAndUp]: {
      flexDirection: 'row',
      justifyContent: 'space-between'
    }
  })
);

const ChallengeDetails = styled.div(({ theme }) => ({
  fontSize: theme.fontSize.small,
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: theme.spacing.xsmall
}));

const TimeDetails = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  fontSize: theme.fontSize.small,
  width: '100%',
  justifyContent: 'space-between',
  paddingTop: theme.spacing.xsmall,
  [theme.mediaQuery.desktopAndUp]: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
    width: 'auto'
  }
}));

const Title = styled.span(({ theme }) => ({
  fontSize: theme.fontSize.medium,
  fontWeight: theme.fontWeight.demiBold,
  paddingRight: 25
}));

const InfoContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  fontWeight: theme.fontWeight.normal,
  [theme.mediaQuery.desktopAndUp]: {
    flexDirection: 'row',
    alignItems: 'center'
  }
}));

const DetailsAndButton = styled.div({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between'
});

const Bold = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.demiBold
}));

const InfoWrapper = styled.div({
  paddingRight: 20
});

const InfoName = styled.span({
  paddingRight: 4
});

function Info({ name, children }: { name: string, children: any }) {
  return (
    <InfoWrapper>
      <InfoName>{name}:</InfoName>
      <Bold>{children}</Bold>
    </InfoWrapper>
  );
}

const Divider = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis5,
  height: 1,
  width: '100%',
  marginTop: theme.spacing.xsmall
}));

const Footer = styled.div(
  {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  ({ theme }) => ({
    backgroundColor: theme.color.white,
    padding: theme.spacing.large
  })
);

const Prize = styled.div({
  display: 'flex',
  alignItems: 'center',
  paddingRight: 20
});

const PrizeLabel = styled.span({
  paddingRight: 4
});

class ChallengeInfoModal extends React.Component<Props, State> {
  props: Props;

  state: State = {
    activeTabItem: 'prizes'
  };

  static defaultProps = {
    isTicketChallenge: false,
    games: [],
    labels: [],
    payoutIcon: undefined,
    scoring: []
  };

  componentDidUpdate(prevProps, prevState) {
    const { fetchGames, games = [], id } = this.props;
    const { activeTabItem } = this.state;

    if (prevState.activeTabItem !== 'games' && activeTabItem === 'games' && games.length === 0) {
      fetchGames(id);
    }
  }

  componentWillUnmount() {
    // if you click the enter button reset the selected challenge
    const { onClose } = this.props;
    onClose();
  }

  // componentDidCatch(error, errorInfo) {
  //   console.log(error, errorInfo);
  // }

  handleChangeTab = tab => {
    this.setState(() => ({ activeTabItem: tab }));
  };

  render() {
    const {
      bonuses,
      cost,
      countries,
      entryCap,
      entryCount,
      games,
      id,
      isTicketChallenge,
      isRacing,
      isUnlimited,
      labels,
      lang,
      link,
      maxEntriesPerPerson,
      name,
      onClose,
      payout,
      payoutIcon,
      payoutMessage,
      prizePool,
      scoring,
      startTime,
      statusId
    } = this.props;
    const { activeTabItem } = this.state;

    return (
      <Modal isOpen bare onCancel={onClose} size="large" lightCloseButton>
        <div>
          <Head>
            <ChallengeDetails>
              <Title>{name}</Title>
              <DetailsAndButton>
                <InfoContainer>
                  <Info name="Entry Cost">
                    <CurrencyIcon />
                    {numeral(cost).format('0,0.[00]')}
                  </Info>
                  <Info name="Payout">{payout}</Info>
                  <Prize>
                    <PrizeLabel>Prize:</PrizeLabel>
                    {isTicketChallenge ? (
                      <Tooltip title={payoutMessage} disabled={!payoutMessage}>
                        <TicketIndicator src={payoutIcon} />
                      </Tooltip>
                    ) : (
                      <React.Fragment>
                        <CurrencyIcon />
                        <Bold>{prizePool}</Bold>
                      </React.Fragment>
                    )}
                  </Prize>
                </InfoContainer>
                <Responsive mobile>
                  <Button kind="primary" link={link}>
                    {t('Enter')}
                  </Button>
                </Responsive>
              </DetailsAndButton>
            </ChallengeDetails>
            <Responsive mobile>
              <Divider />
            </Responsive>
            <TimeDetails>
              {(statusId === SCHEDULED || statusId === OPEN) && (
                <TimerProvider>
                  <Countdown starting={startTime} format="dd:hh:mm:ss" scheme="light" />
                </TimerProvider>
              )}
              <span>
                {moment(startTime)
                  .locale(lang)
                  .format('dddd, MMM Do YYYY - h:mm a')}
              </span>
            </TimeDetails>
          </Head>
          <ChallengeInfo
            activeTabItem={activeTabItem}
            bonuses={bonuses}
            challengeId={id}
            countries={countries}
            entryCap={entryCap}
            entryCount={entryCount}
            games={games}
            isRacing={isRacing}
            isUnlimited={isUnlimited}
            labels={labels}
            maxEntriesPerPerson={maxEntriesPerPerson}
            onChange={this.handleChangeTab}
            onClose={onClose}
            payoutMessage={payoutMessage}
            scheme="white"
            scoring={scoring}
            statusId={statusId}
          />
          <Responsive desktop>
            <Footer>
              <Button kind="primary" link={link}>
                {t('Enter')}
              </Button>
            </Footer>
          </Responsive>
        </div>
      </Modal>
    );
  }
}

export default ChallengeInfoModal;
